// Dependencies
import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  CircularProgress,
  Backdrop,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';

// Components
import { Dialog } from '../../Dialog';

// Services
import apis from '../../../apis';

// Icons
import { ArrowLeftIcon, ArrowRightIcon } from '../../../assets/icons';

// Styles
import styles from './styles';

// Interfaces
import { ITemplate } from '../../../resource';
import { AppContext } from '../../../context';

interface ITemplatesDialogProps {
  open: boolean;
  onClose: () => void;
}

// Export templates dialog
export const TemplatesDialog: FC<ITemplatesDialogProps> = ({ open, onClose }) => {
  // States
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [step, setStep] = useState<'select' | 'config'>('select');
  const [isAutomated, setIsAutomated] = useState<boolean>(false);
  const [automationFrequency, setAutomationFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily');

  const { account, fetchDocuments } = useContext(AppContext);

  // Fetch templates
  const fetchTemplates = async () => {
    const res: ITemplate[] = await apis.template.getTemplates();
    setTemplates(res);
  };

  // Click template handler
  const handleClickTemplate = (id: string) => () => {
    if (id !== selectedTemplateId) {
      setSelectedTemplateId(id);
    } else {
      setSelectedTemplateId(undefined);
    }
  };

  // Get selected template
  const selectedTemplate = templates.find((t) => t._id === selectedTemplateId);

  // Generate document
  const handleGenerateDocument = async () => {
    if (account?.id && selectedTemplateId) {
      try {
        if (!isLoading) setIsLoading(true);
        
        // If template doesn't have automationEnabled, isAutomated is always false
        // If template has automationEnabled, use the user's choice
        const automationConfig = selectedTemplate?.automationEnabled
          ? {
              isAutomated,
              automationFrequency: isAutomated ? automationFrequency : undefined
            }
          : { isAutomated: false };

        await apis.pdf.generateDocument(account?.id, selectedTemplateId, automationConfig);
        account?.id && fetchDocuments(account.id);
        setIsLoading(false);
        setStep('select');
        setIsAutomated(false);
        setAutomationFrequency('daily');
        onClose();
      } catch (e) {
        setIsLoading(false);
      }
    }
  };

  // Handle next button click
  const handleNext = () => {
    if (!selectedTemplateId) return;

    const template = templates.find((t) => t._id === selectedTemplateId);
    if (template?.automationEnabled && step === 'select') {
      setStep('config');
    } else {
      handleGenerateDocument();
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (step === 'config') {
      setStep('select');
      setIsAutomated(false);
      setAutomationFrequency('daily');
    } else {
      onClose();
    }
  };

  // On mounted
  useEffect(() => {
    (async () => {
      await fetchTemplates();
    })();
  }, []);

  // Return templates dialog
  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <Backdrop open={isLoading} sx={styles.loader}>
        <CircularProgress />
      </Backdrop>

      <Typography mb={35} variant="title">
        {step === 'select' ? 'Templates' : `Configure${selectedTemplate ? ` - ${selectedTemplate.name}` : ''}`}
      </Typography>

      {step === 'select' ? (
        <Box component={PerfectScrollbar} sx={styles.scrollbar}>
          <Grid container spacing={[20, 22]}>
            {templates.map(({ _id, name, thumbnail_url, updatedAt, automationEnabled }, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Box
                  sx={(theme) => styles.template(theme, { active: _id === selectedTemplateId })}
                  onClick={handleClickTemplate(_id)}
                >
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    {automationEnabled && (
                        <Box
                          sx={(theme) => styles.automationEnabledBadge(theme)}
                        >
                          <Tooltip title="Automation enabled" arrow placement="top">
                            <AutoAwesomeIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                          </Tooltip>
                        </Box>
                    )}
                    <Box>
                      <img src={thumbnail_url} alt={thumbnail_url} />
                    </Box>
                  </Box>
                  <FormControlLabel checked={_id === selectedTemplateId} control={<Radio />} label={name} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" gap={16}>
          {/* Left side - Template card */}
          {selectedTemplate && (
            <Box sx={styles.templateCard}>
              <Box sx={styles.templateImage}>
                <img src={selectedTemplate.thumbnail_url} alt={selectedTemplate.name} />
              </Box>
            </Box>
          )}

          {/* Right side - Configuration */}
          <Box sx={styles.configSection}>
              <Typography mb={24} variant="title">
                Mode
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 32 }}>
                <RadioGroup value={isAutomated ? 'automation' : 'normal'} onChange={(e) => setIsAutomated(e.target.value === 'automation')}>
                  <FormControlLabel value="normal" control={<Radio />} label="Normal" />
                  <FormControlLabel value="automation" control={<Radio />} label="Automation" />
                </RadioGroup>
              </FormControl>

              {isAutomated && (
                <>
                  <Typography mb={24} variant="title">
                    Frequency
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup value={automationFrequency} onChange={(e) => setAutomationFrequency(e.target.value as 'daily' | 'weekly' | 'monthly' | 'yearly')}>
                      <FormControlLabel value="daily" control={<Radio />} label="Daily" />
                      <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                      <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                      <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
                    </RadioGroup>
                  </FormControl>
                </>
              )}
            </Box>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" mt={40}>
        <Button variant="varied-outlined" startIcon={<ArrowLeftIcon />} onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="varied-outlined"
          endIcon={<ArrowRightIcon />}
          disabled={step === 'select' ? !selectedTemplateId : false}
          onClick={handleNext}
        >
          Next
        </Button>
      </Stack>
    </Dialog>
  );
};
